import { Action, Reducer } from 'redux';
import { addTask } from 'domain-task';

import {
	BaseChatMessage,
	Chat,
	ChatMessage,
	ChatMessageAccess,
	ChatUser,
	EmojiReaction,
	BaseChatUser,
} from '@common/react/components/Chat/Chat';
import { List, transformArrayToList } from '@common/typescript/objects/List';
import { BaseApplicationState, BaseAppThunkAction } from '@common/react/store';
import { RequestType } from '@common/react/components/RequestProvider/RequestProvider';

export interface ChatsState<
	TUser extends BaseChatUser = BaseChatUser,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>
> {
	chats: List<TChat>;
	currentChat: TChat | null;
	isLoading: boolean;
	isMessagesLoading: boolean;
	isMessagesLazy: boolean;
	messages: ChatMessagesState<TChatMessage>;
	chatsLoaded: boolean;
	notViewMessages: Array<number>;
	modalMode: boolean;
}

interface ChatMessagesState<TChatMessage extends ChatMessage = ChatMessage> {
	[id: number]: List<TChatMessage>;
}

export interface ApplicationStateWithChats<
	TUser extends BaseChatUser,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>
> extends BaseApplicationState<TUser> {
	chats: ChatsState<TUser, TChatMessage, TChat>;
}

export enum TypeKeys {
	RECEIVECHATS = 'RECEIVECHATS',
	REQUESTCHATS = 'REQUESTCHATS',
	ADDCHAT = 'ADDCHAT',
	UPDATECHAT = 'UPDATECHAT',
	SELECTCHAT = 'SELECTCHAT',
	DELETECHAT = 'DELETECHAT',
	REQUESTMESSAGES = 'REQUESTMESSAGES',
	RECEIVEMESSAGES = 'RECEIVEMESSAGES',
	RECEIVEMOREMESSAGES = 'RECEIVEMOREMESSAGES',
	ADDMESSAGE = 'ADDMESSAGE',
	UPDATEMESSAGE = 'UPDATEMESSAGE',
	UPDATEMESSAGEVIEWED = 'UPDATEMESSAGEVIEWED',
	SETMESSAGES = 'SETMESSAGES',
	SETCHATS = 'SETCHATS',
	DELETEMESSAGE = 'DELETEMESSAGE',
	ADDREACTION = 'ADDREACTION',
	REMOVEREACTION = 'REMOVEREACTION'
}

interface ReceiveChatsAction<TUser extends BaseChatUser, TChat extends Chat<TUser>> {
	storageName: string;
	type: TypeKeys.RECEIVECHATS;
	items: List<TChat>;
}

interface RequestChatsAction {
	storageName: string;
	type: TypeKeys.REQUESTCHATS;
}

interface RequestChatMessagesAction {
	storageName: string;
	type: TypeKeys.REQUESTMESSAGES;
}

interface ReceiveChatMessagesAction<TUser extends BaseChatUser, TChatMessage extends ChatMessage<TUser>> {
	storageName: string;
	type: TypeKeys.RECEIVEMESSAGES;
	messages: List<TChatMessage>;
	chatId: number;
}

interface ReceiveChatMoreMessagesAction<TUser extends BaseChatUser, TChatMessage extends ChatMessage<TUser>> {
	storageName: string;
	type: TypeKeys.RECEIVEMOREMESSAGES;
	messages: List<TChatMessage>;
	chatId: number;
}

interface SetChatMessagesAction<TUser extends BaseChatUser, TChatMessage extends ChatMessage<TUser>> {
	storageName: string;
	type: TypeKeys.SETMESSAGES;
	messages: List<TChatMessage>;
	chatId: number;
}

interface AddChatAction<TUser extends BaseChatUser, TChat extends Chat<TUser>> {
	storageName: string;
	type: TypeKeys.ADDCHAT;
	chat: TChat;
}

interface AddMessageAction<TUser extends BaseChatUser, TChatMessage extends ChatMessage<TUser>> {
	storageName: string;
	type: TypeKeys.ADDMESSAGE;
	message: TChatMessage;
	increase: boolean;
}

interface UpdateMessageAction<TUser extends BaseChatUser, TChatMessage extends ChatMessage<TUser>> {
	storageName: string;
	type: TypeKeys.UPDATEMESSAGE;
	message: TChatMessage;
}

interface UpdateMessageViewedAction {
	storageName: string;
	type: TypeKeys.UPDATEMESSAGEVIEWED;
	messageViewer: ChatMessageAccess;
	chatId: number;
	messageId: number;
	viewed: boolean;
	addMessageViewer: boolean;
}

interface UpdateChatAction<TUser extends BaseChatUser, TChat extends Chat<TUser>> {
	storageName: string;
	type: TypeKeys.UPDATECHAT;
	chat: TChat;
}

interface SelectChatAction<TUser extends BaseChatUser, TChat extends Chat<TUser>> {
	storageName: string;
	type: TypeKeys.SELECTCHAT;
	chat: TChat;
}

interface DeleteChatAction {
	storageName: string;
	type: TypeKeys.DELETECHAT;
	chatId: number;
}

interface SetChats<TUser extends BaseChatUser, TChat extends Chat<TUser>> {
	storageName: string;
	type: TypeKeys.SETCHATS;
	chats: List<TChat>;
}

interface RemoveMessage<TUser extends BaseChatUser, TChatMessage extends ChatMessage<TUser>> {
	storageName: string;
	type: TypeKeys.DELETEMESSAGE;
	message: TChatMessage;
	chatId: number;
}

interface AddReaction {
	storageName: string;
	type: TypeKeys.ADDREACTION;
	chatId: number;
	messageId: number;
	emojiReaction: EmojiReaction;
}

interface RemoveReaction {
	storageName: string;
	type: TypeKeys.REMOVEREACTION;
	chatId: number;
	messageId: number;
	reactionId: number;
}

type KnownPageAction<
	TUser extends BaseChatUser = BaseChatUser,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>
> = ReceiveChatsAction<TUser, TChat>
	| RequestChatsAction
	| RequestChatMessagesAction
	| ReceiveChatMessagesAction<TUser, TChatMessage>
	| AddChatAction<TUser, TChat>
	| AddMessageAction<TUser, TChatMessage>
	| UpdateMessageAction<TUser, TChatMessage>
	| UpdateMessageViewedAction
	| UpdateChatAction<TUser, TChat>
	| SelectChatAction<TUser, TChat>
	| ReceiveChatMoreMessagesAction<TUser, TChatMessage>
	| DeleteChatAction
	| SetChatMessagesAction<TUser, TChatMessage>
	| SetChats<TUser, TChat>
	| RemoveMessage<TUser, TChatMessage>
	| AddReaction
	| RemoveReaction;

export interface ChatsActionCreators<
	TUser extends BaseChatUser,
	TApplicationState extends ApplicationStateWithChats<TUser>,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
> {
	loadChats: (request: RequestType, requestName: string, storageName: string, onLoad?: (data: List<TChat>) => void, data?: any) =>
		BaseAppThunkAction<KnownPageAction<TUser, TChatMessage, TChat>, TUser, TApplicationState>;
	loadMessages: (request: RequestType, requestName: string, storageName: string, chatId: number, loadMore: boolean, count?: number) =>
		BaseAppThunkAction<KnownPageAction<TUser, TChatMessage, TChat>, TUser, TApplicationState>;
	addChat: (chat: TChat, storageName: string) => void;
	removeChat: (chatId: number, storageName: string) => void;
	updateChat: (chat: TChat, storageName: string) => void;
	addMessage: (request: RequestType, requestName: string, storageName: string, message: TChatMessage, increaseCounter: boolean,
		preventAddChat?: boolean) => void;
	updateMessage: (message: TChatMessage, storageName: string) => void;
	updateCurrentChat: (chat: Partial<TChat> | ((prev: TChat | null) => TChat), storageName: string) => void;
	updateMessageContent: (message: BaseChatMessage, storageName: string) => void;
	updateMessageViewed: (messageViewer: ChatMessageAccess, chatId: number, messageId: number, viewed: boolean, storageName: string,
		addMessageViewer?: boolean) => void;
	updateAllMessages: (
		handler: (messages: List<TChatMessage>, chatId: number | string) => List<TChatMessage>,
		storageName: string,
	) => void;
	selectChat: (chat: TChat | null, storageName: string) => void;
	changeChatCounter: (chatId: number, value: number, storageName: string) => void;
	updateChatCounter: (chatId: number, unviewedMessagesCount: number, storageName: string) => void;
	addUserToChat: (chatUser: ChatUser, storageName: string) => void;
	removeUserFromChat: (chatUser: ChatUser, storageName: string) => void;
	setMessages: (messages: List<TChatMessage>, chatId: number, storageName: string) => void;
	setChats: (chats: List<TChat> | ((prev: List<TChat>) => List<TChat>), storageName: string) => void;
	removeMessage: (request: RequestType, requestName: string, storageName: string, messages: TChatMessage, chatId: number) => void;
	addReaction: (chatId: number, messageId: number, emojiReaction: EmojiReaction, storageName: string) => void;
	removeReaction: (chatId: number, messageId: number, reactionId: number, storageName: string) => void;
	[key: string]: any;
}

export type ChatActions<
	TUser extends BaseChatUser = BaseChatUser,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
> = ChatsActionCreators<
	TUser,
	ApplicationStateWithChats<TUser>,
	TChatMessage,
	TChat>;

const findChat = <TUser extends BaseChatUser = BaseChatUser, TChat extends Chat<BaseChatUser> = Chat<BaseChatUser>>(
	chats: Array<TChat>,
	chatId: number,
): TChat | undefined => chats.find((item: TChat) => item.id === chatId);

export const getActionCreators = <
	TUser extends BaseChatUser,
	TApplicationState extends ApplicationStateWithChats<TUser>,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
>() => {
	return {
		loadChats: (
			request: RequestType,
			requestName: string,
			storageName: string,
			onLoad?: (data: List<TChat>) => void,
			data?: any,
		): BaseAppThunkAction<KnownPageAction<TUser, TChatMessage, TChat>, TUser, TApplicationState> =>
			(dispatch, getState) => {
				const state = getState()[storageName];

				if (!state.chatsLoaded) {
					const fetchTask = request<List<TChat>>(requestName, { ...data }).then((data) => {
						const res = {
							...data,
							items: data.list.map((item) => ({
								...item,
								unviewedMessagesCount: item.unviewedMessagesCount > 0 ? item.unviewedMessagesCount : 0,
							})),
						};
						dispatch({ type: TypeKeys.RECEIVECHATS, items: res, storageName });
						onLoad && onLoad(res as List<TChat>);
					});

					dispatch({ type: TypeKeys.REQUESTCHATS, storageName });

					addTask(fetchTask);

					return fetchTask;
				}

				return Promise.resolve(null);
			},
		setChats: (chats: List<TChat> | ((prev: List<TChat>) => List<TChat>), storageName: string) => (dispatch, getState) => {
			const state = getState();
			const prevChatList = (state[storageName] as ChatsState<TUser, TChatMessage, TChat>)?.chats;
			const newChats = typeof chats === 'function' ? chats(prevChatList) : chats;
			dispatch({ type: TypeKeys.SETCHATS, chats: newChats, storageName });
		},
		setMessages: (messages: List<TChatMessage>, chatId: number, storageName: string) => (dispatch, getState) => {
			dispatch({
				type: TypeKeys.SETMESSAGES, messages, chatId, storageName,
			});
		},
		loadMessages: (
			request: RequestType,
			requestName: string,
			storageName: string,
			chatId: number,
			loadMore: boolean,
			count = 20,
		): BaseAppThunkAction<KnownPageAction<TUser, TChatMessage, TChat>, TUser, TApplicationState> =>
			(dispatch, getState) => {
				const state = getState();
				const messages = state[storageName].messages[chatId];

				if (!messages || loadMore) {
					const fetchTask = request(requestName, {
						chatId,
						count,
						offset: loadMore ? messages.offset + count : 0,
					}).then((data) => {
						const list = data as List<TChatMessage>;

						list.list.reverse();

						if (loadMore) {
							dispatch({
								type: TypeKeys.RECEIVEMOREMESSAGES, messages: list, chatId, storageName,
							});
						} else {
							dispatch({
								type: TypeKeys.RECEIVEMESSAGES, messages: list, chatId, storageName,
							});
						}
					});

					dispatch({ type: TypeKeys.REQUESTMESSAGES, storageName });

					addTask(fetchTask);

					return fetchTask;
				}
			},
		addChat: (chat: TChat, storageName: string) => (dispatch, getState) => {
			dispatch({ type: TypeKeys.ADDCHAT, chat, storageName });
		},
		changeChatCounter: (chatId: number, value: number, storageName: string) => (dispatch, getState) => {
			const state = getState();
			const chat = findChat(state[storageName].chats.list, chatId);

			if (chat) {
				dispatch({ type: TypeKeys.UPDATECHAT, storageName, chat: { ...chat, unviewedMessagesCount: chat.unviewedMessagesCount + value } });
			}
		},
		updateChatCounter: (chatId: number, unviewedMessagesCount: number, storageName: string) => (dispatch, getState) => {
			const state = getState();
			const chat = findChat(state[storageName].chats.list, chatId);

			if (chat) {
				dispatch({
					type: TypeKeys.UPDATECHAT,
					storageName,
					chat: { ...chat, unviewedMessagesCount: unviewedMessagesCount > 0 ? unviewedMessagesCount : 0 },
				});
			}
		},
		updateChat: (chat: TChat, storageName) => (dispatch, getState) => {
			dispatch({ type: TypeKeys.UPDATECHAT, chat, storageName });
		},
		updateCurrentChat: (chat: Partial<TChat> | ((prev: TChat | null) => TChat), storageName) => (dispatch, getState) => {
			const state = getState();
			const prev = (state[storageName] as ChatsState<TUser, TChatMessage, TChat>).currentChat;
			if (!prev) {
				return;
			}
			const newChat = typeof chat === 'function' ? chat(prev) : { ...prev, ...chat };
			dispatch({ type: TypeKeys.UPDATECHAT, chat: newChat, storageName });
		},
		addMessage: (
			request: RequestType,
			requestName: string,
			storageName: string,
			message: TChatMessage,
			increaseCounter: boolean,
			preventAddChat: boolean | undefined,
		) => (dispatch, getState) => {
			const state = getState();

			const _increaseCounter = increaseCounter && !message.viewed;

			if (state[storageName].messages[message.chatId]) {
				dispatch({
					type: TypeKeys.ADDMESSAGE, message, increase: _increaseCounter, storageName,
				});
			} else if (!preventAddChat) {
				const fetchTask = request<TChat>(requestName, {
					id: message.chatId,
				}).then((res) => {
					if (res) {
						dispatch({ type: TypeKeys.ADDCHAT, chat: res, storageName });

						res.messages.list.reverse().splice(res.messages.list.length - 1, 1);
						dispatch({
							type: TypeKeys.RECEIVEMESSAGES, messages: res.messages, chatId: res.id, storageName,
						});

						dispatch({
							type: TypeKeys.ADDMESSAGE, message, increase: _increaseCounter, storageName,
						});
					}
				});

				addTask(fetchTask);

				return fetchTask;
			}
		},
		updateMessageContent: (message: BaseChatMessage, storageName: string) => (dispatch, getState) => {
			const state: TApplicationState = getState();
			const messages = state[storageName]?.messages?.[message.chatId];

			const updatedMessage = messages?.list?.find((q) => q.id === message.id);

			if (updatedMessage === undefined) return;

			const lastMessage = messages.list[messages.list.length - 1] || null;

			const content = {
				text: message.text,
				files: typeof (message.files) !== 'undefined' && message.files !== null
					? message.files
					: updatedMessage.files,
				loading: message.loading,
			};

			dispatch({ type: TypeKeys.UPDATEMESSAGE, message: { ...updatedMessage, ...content }, storageName });
			if (lastMessage?.id === message?.id) {
				const chat = findChat(state[storageName].chats.list, message.chatId);

				dispatch({ type: TypeKeys.UPDATECHAT, chat: { ...chat, lastMessage: { ...updatedMessage, ...content } }, storageName });
			}
		},
		updateMessage: (message: TChatMessage, storageName: string) => (dispatch, getState) => {
			const state: TApplicationState = getState();
			const messages = state[storageName]?.messages?.[message.chatId];
			const lastMessage = messages.list[messages.list.length - 1] || null;

			dispatch({ type: TypeKeys.UPDATEMESSAGE, message, storageName });
			if (lastMessage?.id === message?.id) {
				const chat = findChat(state[storageName].chats.list, message.chatId);

				dispatch({ type: TypeKeys.UPDATECHAT, chat: { ...chat, lastMessage: message }, storageName });
			}
		},
		updateMessageViewed: (
			messageViewer: ChatMessageAccess,
			chatId: number,
			messageId: number,
			viewed: boolean,
			storageName: string,
			addMessageViewer: boolean = false,
		) =>
			(dispatch, getState) => {
				dispatch({
					type: TypeKeys.UPDATEMESSAGEVIEWED, messageViewer, chatId, messageId, viewed, storageName, addMessageViewer,
				});
			},
		updateAllMessages: (
			handler: (messages: List<TChatMessage>, chatId: number | string) => List<TChatMessage>,
			storageName: string,
		) =>
			(dispatch, getState) => {
				const state: TApplicationState = getState();
				const messages = (state[storageName] as ChatsState<TUser, TChatMessage, TChat>)?.messages;
				try {
					Object.keys(messages).forEach((chatId) => {
						dispatch({
							type: TypeKeys.SETMESSAGES,
							messages: handler(messages[chatId], chatId),
							chatId,
							storageName,
						});
					});
				} catch (e) {
					console.log(e);
				}
			},
		selectChat: (chat: TChat | null, storageName: string) => (dispatch, getState) => {
			dispatch({ type: TypeKeys.SELECTCHAT, chat, storageName });
		},
		removeChat: (chatId: number, storageName: string) => (dispatch, getState) => {
			dispatch({ type: TypeKeys.DELETECHAT, chatId, storageName });
		},
		addUserToChat: (chatUser: ChatUser, storageName: string) => (dispatch, getState) => {
			const state: TApplicationState = getState();
			const chat = findChat(state[storageName].chats.list, chatUser.chatId);

			if (chat) {
				if (chat.contactsIds.indexOf(chatUser.userId) === -1) {
					const newChatInfo = {
						contacts: [...chat.contacts, chatUser.user],
						contactsIds: [chat.contactsIds ? { ...chat.contactsIds } : [], chatUser.userId],
					};

					dispatch({ type: TypeKeys.UPDATECHAT, chat: { ...chat, ...newChatInfo }, storageName });
				}
			}
			// } else if (chatUser.chat) {
			//  dispatch({type: TypeKeys.ADDCHAT, chat: chatUser.chat});
			// }
		},
		removeUserFromChat: (chatUser: ChatUser, storageName: string) => (dispatch, getState) => {
			const state: TApplicationState = getState();
			const chat = findChat(state[storageName].chats.list, chatUser.chatId);

			if (chat) {
				const newChatInfo = {
					contacts: chat.contacts.filter((contact: BaseChatUser) => contact.id !== chatUser.userId),
					contactsIds: chat.contactsIds.filter((id: number) => id !== chatUser.userId),
				};

				dispatch({ type: TypeKeys.UPDATECHAT, chat: { ...chat, ...newChatInfo }, storageName });
			}
		},
		removeMessage: (
			request: RequestType,
			requestName: string,
			storageName: string,
			message: TChatMessage,
			chatId: number,
		) => (dispatch, getState) => {
			const state = getState();
			const messages = state[storageName]?.messages?.[message.chatId];

			if (messages && !((messages?.count === undefined || messages?.count > 20) && messages?.list.length <= 20)) {
				dispatch({
					type: TypeKeys.DELETEMESSAGE, chatId, message, storageName,
				});
			} else {
				const fetchTask = request<TChat>(requestName, {
					id: message.chatId,
				}).then((res) => {
					if (res) {
						res.messages.list.reverse();
						const lastMessage = res.messages.list[res.messages.list.length - 1] || null;
						dispatch({ type: TypeKeys.UPDATECHAT, chat: { ...res, lastMessage }, storageName });

						dispatch({
							type: TypeKeys.RECEIVEMESSAGES, messages: res.messages, chatId: res.id, storageName,
						});
					}
				});

				addTask(fetchTask);

				return fetchTask;
			}
		},
		addReaction: (chatId: number, messageId: number, emojiReaction: EmojiReaction, storageName: string) => (dispatch, getState) => {
			dispatch({
				type: TypeKeys.ADDREACTION, chatId, messageId, emojiReaction, storageName,
			});
		},
		removeReaction: (chatId: number, messageId: number, reactionId: number, storageName: string) => (dispatch, getState) => {
			dispatch({
				type: TypeKeys.REMOVEREACTION, chatId, messageId, reactionId, storageName,
			});
		},
	};
};

export const getReducer = <TUser extends BaseChatUser>(storageName: string = 'chats'): Reducer<ChatsState> => {
	return (state: ChatsState = {
		isLoading: false,
		isMessagesLoading: false,
		isMessagesLazy: false,
		chats: transformArrayToList([]),
		messages: {},
		currentChat: null,
		chatsLoaded: false,
		notViewMessages: [],
		modalMode: false,
	}, incomingAction: Action) => {
		const action = incomingAction as KnownPageAction;

		if (!(action.type in TypeKeys)) {
			return state || {
				isLoading: false,
				chats: transformArrayToList([]),
				messages: {},
			};
		}

		if (!action.storageName || action.storageName !== storageName) {
			return state;
		}

		let messages;

		switch (action.type) {
			case TypeKeys.REQUESTCHATS:
				return { ...state, isLoading: true };
			case TypeKeys.REQUESTMESSAGES:
				return { ...state, isMessagesLoading: true };
			case TypeKeys.ADDCHAT:
				if (findChat(state.chats.list, action.chat.id)) {
					return state;
				}
				const newChats = { ...state.chats, count: state.chats.count, list: [action.chat, ...state.chats.list] };
				return { ...state, chats: newChats };
			case TypeKeys.UPDATECHAT:
				return {
					...state,
					chats: {
						...state.chats,
						list: state.chats.list.map((chat: Chat) => (chat.id === action.chat.id ? { ...chat, ...action.chat } : chat)),
					},
					currentChat: state.currentChat && state.currentChat.id === action.chat.id
						? { ...state.currentChat, ...action.chat } : state.currentChat,
				};
			case TypeKeys.SELECTCHAT:
				return { ...state, currentChat: action.chat };
			case TypeKeys.DELETECHAT:
				return {
					...state,
					chats: {
						...state.chats,
						count: state.chats.count - 1,
						list: state.chats.list.filter((chat: Chat) => chat.id !== action.chatId),
					},
				};
			case TypeKeys.RECEIVECHATS:
				return {
					...state, isLoading: false, chats: action.items, chatsLoaded: true,
				};
			case TypeKeys.SETCHATS:
				return {
					...state, isLoading: false, chats: action.chats, chatsLoaded: true,
				};
			case TypeKeys.RECEIVEMESSAGES:
				return {
					...state,
					isMessagesLoading: false,
					messages: {
						...state.messages,
						[action.chatId]: action.messages,
					},
				};
			case TypeKeys.RECEIVEMOREMESSAGES:
				return {
					...state,
					isMessagesLoading: false,
					messages: {
						...state.messages,
						[action.chatId]: {
							...state.messages[action.chatId],
							list: [...action.messages.list, ...state.messages[action.chatId].list],
							offset: action.messages.offset,
						},
					},
				};
			case TypeKeys.SETMESSAGES:
				return {
					...state,
					isMessagesLoading: false,
					messages: {
						...state.messages,
						[action.chatId]: {
							...state.messages[action.chatId],
							list: [...action.messages.list],
							offset: action.messages.offset,
						},
					},
				};
			case TypeKeys.ADDMESSAGE:
				messages = state.messages[action.message.chatId];

				const newList = [...messages.list, action.message];

				const chatIndex = state.chats.list.findIndex((item: Chat) => item.id === action.message.chatId);

				const chat = state.chats.list[chatIndex];

				return {
					...state,
					chats: {
						...state.chats,
						list: [
							{
								...chat,
								lastMessage: action.message,
								unviewedMessagesCount: chat.unviewedMessagesCount + (action.increase ? 1 : 0),
							},
							...state.chats.list.slice(0, chatIndex),
							...state.chats.list.slice(chatIndex + 1),
						],
					},
					notViewMessages: action.increase ? state.notViewMessages.concat(action.message.id) : state.notViewMessages,
					messages: {
						...state.messages,
						[action.message.chatId]: { ...messages, list: newList, count: state.messages[action.message.chatId].count + 1 },
					},
				};
			case TypeKeys.UPDATEMESSAGE:
				messages = state.messages[action.message.chatId];

				return {
					...state,
					isMessagesLoading: false,
					messages: {
						...state.messages,
						[action.message.chatId]: {
							...messages,
							list: messages.list.map((item: ChatMessage) => {
								if (action.message.id === item.id) {
									return action.message;
								}

								return item;
							}),
						},
					},
				};
			case TypeKeys.UPDATEMESSAGEVIEWED:
				messages = state.messages[action.chatId];
				const notViewMessages = action.viewed ? state.notViewMessages.filter((messageId) => messageId !== action.messageId)
					: state.notViewMessages;
				return {
					...state,
					isMessagesLoading: false,
					notViewMessages,
					messages: {
						...state.messages,
						[action.chatId]: {
							...messages,
							list: messages.list.map((item: ChatMessage) => {
								if (item.id === action.messageId) {
									return {
										...item,
										messageViewers: action.addMessageViewer
											? item.messageViewers
												? [...item.messageViewers, action.messageViewer]
												: [action.messageViewer]
											: item.messageViewers,
										viewed: action.viewed,
									};
								}

								return item;
							}),
						},
					},
				};
			case TypeKeys.DELETEMESSAGE:
				messages = state.messages[action.chatId];

				const deleteMessage = messages.list.find((item) => item.id === action.message.id);

				if (!deleteMessage) {
					return state;
				}

				const currentChatIndex = state.chats.list.findIndex((item: Chat) => item.id === action.message.chatId);
				const currentChat = state.chats.list[currentChatIndex];
				const newLastMessage = currentChat.lastMessage?.id === deleteMessage?.id
					? messages.list?.[messages.list.length - 2] || null : currentChat.lastMessage;

				return {
					...state,
					chats: {
						...state.chats,
						list: [
							{
								...currentChat,
								lastMessage: newLastMessage,
								unviewedMessagesCount: currentChat.unviewedMessagesCount + (action.message.viewed ? 0 : -1),
							},
							...state.chats.list.slice(0, currentChatIndex),
							...state.chats.list.slice(currentChatIndex + 1),
						],
					},
					isMessagesLoading: false,
					messages: {
						...state.messages,
						[action.chatId]: {
							...messages,
							count: messages.count - 1,
							offset: messages.offset - 1,
							list: messages.list.filter((item: ChatMessage) => item.id !== action.message.id),
						},
					},
				};
			case TypeKeys.ADDREACTION: {
				messages = state.messages[action.chatId];
				const newEmojiReaction = { ...action.emojiReaction, animate: action.emojiReaction?.animate ?? true };
				return {
					...state,
					messages: {
						...state.messages,
						[action.chatId]: {
							...messages,
							list: messages.list.map((message) => (message.id !== action.messageId
								? message
								: {
									...message,
									emojiReactions: message.emojiReactions === null
										? [newEmojiReaction]
										: message.emojiReactions.map((emojiReaction) => emojiReaction.id).indexOf(action.emojiReaction.id) < 0
											? [...message.emojiReactions, newEmojiReaction]
											: message.emojiReactions.map((emojiReaction) => (emojiReaction.id !== action.emojiReaction.id
												? emojiReaction
												: newEmojiReaction)),
								})),
						},
					},
				};
			}
			case TypeKeys.REMOVEREACTION:
				messages = state.messages[action.chatId];
				return {
					...state,
					messages: {
						...state.messages,
						[action.chatId]: {
							...messages,
							list: messages.list.map((message) => (message.id !== action.messageId
								? message
								: {
									...message,
									emojiReactions: message.emojiReactions.filter((emojiReaction) => emojiReaction.id !== action.reactionId),
								})),
						},
					},
				};
			default:
				return state;
		}
	};
};
